import React, { useRef, useState } from 'react';
import { MdUploadFile, MdCheckCircle, MdWarningAmber } from 'react-icons/md';
import API from '../../services/api';
import ChangeSummary from './ChangeSummary';

// Publish a signed policy document. Admin only, and in two stages.
//
// STAGE 1 PARSES AND STORES NOTHING. Publishing replaces the text the Shareholder reads as
// the governing document, and a Word file without proper heading styles renders as one
// undifferentiated block. Showing what was extracted BEFORE anything is written means that
// is caught by the person uploading rather than by whoever opens the portal next. The
// server enforces the same split — the preview endpoint has no write path at all.
//
// The control is hidden from non-admins as a courtesy. IsDeltaAdmin refuses the request
// regardless, which is the actual protection: a hidden button stops a person, not a request.

export default function PolicyUpload({ reference, onPublished, isAdmin }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState('');
  // Draft is the default for everyone. Publishing straight into force bypasses review
  // entirely, so it is an administrator's act for a document that is already signed.
  const [mode, setMode] = useState('draft');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const reset = () => {
    setFile(null); setPreview(null); setError(''); setDone(false); setVersion('');
    if (fileRef.current) fileRef.current.value = '';
  };

  // The file is passed in, NOT read from state. `choose` calls this immediately after
  // setFile(), and a state update has not committed by then — reading `file` from the
  // closure saw null, returned early, and left no preview and no error. The upload button
  // lives inside the preview panel, so the whole screen simply had no way forward.
  const send = async (isPreview, chosen = file) => {
    if (!chosen) return null;
    const body = new FormData();
    body.append('file', chosen);
    if (version) body.append('doc_version', version);
    body.append('mode', mode);
    const path = `/api/delta/policies/${encodeURIComponent(reference)}/document/${isPreview ? 'preview/' : ''}`;
    const r = await API.post(path, body, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  };

  const choose = async (e) => {
    const f = e.target.files?.[0];
    setError(''); setDone(false); setPreview(null); setFile(f || null);
    if (!f) return;
    setBusy(true);
    try {
      const result = await send(true, f);
      setPreview(result);
      if (!result) setError('That file could not be read. Try selecting it again.');
    } catch (err) {
      // The server authors these messages: they name the extension or size that refused it.
      setError(err?.response?.data?.detail || 'That file could not be read.');
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true); setError('');
    try {
      await send(false);
      setDone(true);
      setPreview(null);
      if (onPublished) onPublished();
    } catch (err) {
      setError(err?.response?.data?.detail || 'The document could not be published.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-6 rounded-lg border border-dashed border-gray-300 p-3 dark:border-gray-700 sm:p-4">
      <div className="flex items-center gap-2">
        <MdUploadFile size={18} className="shrink-0 text-gray-400" />
        <h2 className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">
          {mode === 'publish' ? 'Publish a signed document' : 'Upload a draft'}
        </h2>
      </div>
      <p className="mt-1 text-[11.5px] leading-relaxed text-gray-500 dark:text-gray-400">
        Word (.docx) is read into the on-screen text as well as attached. A PDF is attached
        only. Use Heading&nbsp;1/2/3 styles, and type clause numbers into the headings if you
        want them to appear.
      </p>

      {isAdmin && (
        <div className="mt-2 flex gap-1 rounded-md border border-gray-200 p-0.5 dark:border-gray-700">
          {[['draft', 'Start a draft'], ['publish', 'Publish directly']].map(([m, label]) => (
            <button key={m} type="button" onClick={() => { setMode(m); setPreview(null); }}
                    className={`flex-1 rounded px-2 py-1.5 text-[11.5px] transition-colors ${
                      mode === m ? 'bg-brand-gold/10 font-semibold text-brand-gold'
                                 : 'text-gray-500 hover:text-gray-900 dark:text-gray-400'}`}>
              {label}
            </button>
          ))}
        </div>
      )}
      {mode === 'publish' && (
        <p className="mt-2 text-[11.5px] leading-relaxed text-amber-700 dark:text-amber-400">
          This skips review and replaces what the Shareholder reads immediately. Use it only
          for a document that is already signed.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".docx,.pdf" onChange={choose} disabled={busy}
               className="min-w-0 flex-1 text-[12px] text-gray-600 file:mr-2 file:rounded-md file:border file:border-gray-200 file:bg-transparent file:px-2.5 file:py-1.5 file:text-[12px] file:text-gray-700 dark:text-gray-400 dark:file:border-gray-700 dark:file:text-gray-200" />
        <label className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
          Version
          <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0"
                 className="w-16 rounded-md border border-gray-200 bg-transparent px-2 py-1 text-[12px] text-gray-900 outline-none focus:border-brand-gold dark:border-gray-700 dark:text-gray-100" />
        </label>
      </div>

      {busy && <p className="mt-2 text-[12px] text-gray-500">Reading…</p>}

      {error && (
        <div className="mt-3 rounded border-l-2 border-rose-500 bg-rose-50 px-3 py-2 text-[12px] text-rose-900 dark:bg-rose-500/10 dark:text-rose-200">
          {error}
        </div>
      )}

      {done && (
        <div className="mt-3 flex items-start gap-2 rounded border-l-2 border-emerald-500 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-200">
          <MdCheckCircle size={16} className="mt-0.5 shrink-0" />
          {mode === 'publish' ? 'Published. The page below now shows the new document.'
            : 'Draft saved. Submit it for review in the trail below.'}
        </div>
      )}

      {preview && (
        <div className="mt-3 rounded-md border border-gray-200 p-3 dark:border-gray-800">
          <div className="text-[9px] uppercase tracking-[2px] text-gray-400 dark:text-gray-500">
            Nothing has been saved yet
          </div>
          <p className="mt-1 text-[12px] text-gray-700 dark:text-gray-200">
            <span className="font-semibold">{preview.filename}</span>
            {preview.will_extract
              ? ` — ${preview.section_count} section${preview.section_count === 1 ? '' : 's'} read.`
              : ' — will be attached without an on-screen rendering.'}
            {preview.replaces_section_count > 0 && (
              <span className="text-amber-700 dark:text-amber-400">
                {' '}This replaces the {preview.replaces_section_count} section
                {preview.replaces_section_count === 1 ? '' : 's'} currently shown.
              </span>
            )}
          </p>

          {preview.warnings?.map((w) => (
            <div key={w} className="mt-2 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-amber-700 dark:text-amber-400">
              <MdWarningAmber size={14} className="mt-0.5 shrink-0" />
              <span>{w}</span>
            </div>
          ))}

          {preview.outline?.length > 0 && (
            <div className="scrollbar-thin mt-2.5 max-h-52 overflow-y-auto rounded bg-gray-50 p-2 dark:bg-white/[0.03]">
              {preview.outline.map((s, i) => (
                <div key={i} className="truncate text-[11.5px] leading-relaxed text-gray-600 dark:text-gray-300"
                     style={{ paddingLeft: `${(s.depth - 1) * 12}px` }}>
                  {s.number && <span className="mr-1.5 font-semibold tabular-nums">{s.number}</span>}
                  {s.heading || <span className="italic text-gray-400">(no heading)</span>}
                </div>
              ))}
            </div>
          )}

          {/* What this upload would change, BEFORE it is committed. The whole reason the
              upload is two-stage. */}
          <ChangeSummary summary={preview} />

          <div className="mt-3 flex gap-2">
            <button type="button" onClick={publish} disabled={busy}
                    className="rounded-md bg-brand-gold px-3 py-2 text-[12px] font-medium text-white disabled:opacity-40">
              {mode === 'publish' ? 'Publish' : 'Save draft'}
            </button>
            <button type="button" onClick={reset} disabled={busy}
                    className="rounded-md border border-gray-200 px-3 py-2 text-[12px] text-gray-600 dark:border-gray-700 dark:text-gray-300">
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
